import React from 'react';
import PropTypes from 'prop-types';
import { FormHelperText } from '@material-ui/core';
import {
  Flex,
  Multiline,
  Select,
  TextField,
} from '@knockrentals/knock-shared-web-v2';
import {
  makeCss,
  MultiSelectChips,
} from '../../../../../../../knock-design-system';
import { DividedTabSection } from '../../TabSections';
import { formatFieldValue } from '../utils';
import { propertyDetailsPropTypes } from '../../../../prop-types';
import { SECTIONS } from '../../constants';
import {
  CITY,
  DESCRIPTION,
  FIELD_LABELS,
  NEIGHBORHOOD,
  PROPERTY_NAME,
  STATE,
  STATE_OPTIONS,
  STREET,
  TOTAL_UNITS,
  UNIT_TYPES,
  UNIT_TYPE_OPTIONS,
  YEAR_BUILT,
  ZIP,
} from './constants';

const useCss = makeCss({
  city: {
    flex: 1,
  },
  inputSmall: {
    width: '100px',
    flex: 'none',
  },
  neighborhood: {
    width: '216px',
    flex: 1,
  },
  state: {
    flex: 'none',
    '& .MuiSelect-select': {
      minWidth: '100px',
    },
  },
});

const { DESCRIPTION: SECTION_DESCRIPTION, TITLE } = SECTIONS.PROPERTY_DETAILS;

const PropertyDetails = ({
  clearFieldErrorMessage,
  propertyDetails = {},
  updatePropertyDetails,
  validationErrorMessages,
}) => {
  const css = useCss();

  const handleOnBlur = (e) => {
    const { name, value } = e.target;

    updatePropertyDetails({ [name]: value.trim() });
  };
  const handleOnFocus = (e) => {
    const { name } = e.target;
    clearFieldErrorMessage(name);
  };

  const handleSelectOnChange = (value, e) => {
    const { name } = e.target;
    updatePropertyDetails({ [name]: value });
  };

  const handleStateOnFocus = () => {
    clearFieldErrorMessage(STATE);
  };

  const handleUnitTypesOnChange = (unitTypes) => {
    updatePropertyDetails({ unitTypes });
  };

  const handleUpdatePropertyDetails = (_, e) => {
    const { name, value } = e.target;

    const formattedValue = formatFieldValue(name, value);
    updatePropertyDetails({ [name]: formattedValue });
  };

  return (
    <DividedTabSection description={SECTION_DESCRIPTION} title={TITLE}>
      <Flex flexDirection="column" gap={24}>
        <Flex flex="1" gap={16}>
          <TextField
            error={Boolean(validationErrorMessages[PROPERTY_NAME])}
            fullWidth
            helperText={validationErrorMessages[PROPERTY_NAME]}
            id={PROPERTY_NAME}
            label={FIELD_LABELS[PROPERTY_NAME]}
            name={PROPERTY_NAME}
            onBlur={handleOnBlur}
            onChange={handleUpdatePropertyDetails}
            onFocus={handleOnFocus}
            value={propertyDetails[PROPERTY_NAME]}
            variant="outlined"
          />

          <div css={css.inputSmall}>
            <TextField
              error={Boolean(validationErrorMessages[YEAR_BUILT])}
              helperText={validationErrorMessages[YEAR_BUILT]}
              id={YEAR_BUILT}
              label={FIELD_LABELS[YEAR_BUILT]}
              name={YEAR_BUILT}
              onChange={handleUpdatePropertyDetails}
              onFocus={handleOnFocus}
              value={propertyDetails[YEAR_BUILT] || ''}
              variant="outlined"
            />
          </div>

          <div css={css.inputSmall}>
            <TextField
              id={TOTAL_UNITS}
              label={FIELD_LABELS[TOTAL_UNITS]}
              name={TOTAL_UNITS}
              onChange={handleUpdatePropertyDetails}
              value={propertyDetails[TOTAL_UNITS]}
              variant="outlined"
            />
          </div>
        </Flex>

        <TextField
          error={Boolean(validationErrorMessages[STREET])}
          helperText={validationErrorMessages[STREET]}
          id={STREET}
          label={FIELD_LABELS[STREET]}
          name={STREET}
          onBlur={handleOnBlur}
          onChange={handleUpdatePropertyDetails}
          onFocus={handleOnFocus}
          value={propertyDetails[STREET]}
          variant="outlined"
        />

        <Flex gap={16}>
          <div css={css.city}>
            <TextField
              error={Boolean(validationErrorMessages[CITY])}
              fullWidth
              helperText={validationErrorMessages[CITY]}
              id={CITY}
              label={FIELD_LABELS[CITY]}
              name={CITY}
              onBlur={handleOnBlur}
              onChange={handleUpdatePropertyDetails}
              onFocus={handleOnFocus}
              value={propertyDetails[CITY]}
              variant="outlined"
            />
          </div>

          <div css={css.state}>
            <Select
              error={Boolean(validationErrorMessages[STATE])}
              aria-label={FIELD_LABELS[STATE]}
              label={FIELD_LABELS[STATE]}
              name={STATE}
              onChange={handleSelectOnChange}
              onFocus={handleStateOnFocus}
              options={STATE_OPTIONS}
              value={propertyDetails[STATE]}
              variant="outlined"
            />
            {validationErrorMessages[STATE] && (
              <FormHelperText
                aria-label="state validation error message"
                className="MuiFormHelperText-contained"
                error
              >
                {validationErrorMessages[STATE]}
              </FormHelperText>
            )}
          </div>

          <div css={css.inputSmall}>
            <TextField
              error={Boolean(validationErrorMessages[ZIP])}
              fullWidth
              helperText={validationErrorMessages[ZIP]}
              id={ZIP}
              label={FIELD_LABELS[ZIP]}
              name={ZIP}
              onBlur={handleOnBlur}
              onChange={handleUpdatePropertyDetails}
              onFocus={handleOnFocus}
              value={propertyDetails[ZIP] || ''}
              variant="outlined"
            />
          </div>

          <div css={css.neighborhood}>
            <TextField
              fullWidth
              id={NEIGHBORHOOD}
              label={FIELD_LABELS[NEIGHBORHOOD]}
              name={NEIGHBORHOOD}
              onBlur={handleOnBlur}
              onChange={handleUpdatePropertyDetails}
              value={propertyDetails[NEIGHBORHOOD]}
              variant="outlined"
            />
          </div>
        </Flex>

        <MultiSelectChips
          handleOnChange={handleUnitTypesOnChange}
          id={UNIT_TYPES}
          label={FIELD_LABELS[UNIT_TYPES]}
          limitTags={4}
          options={UNIT_TYPE_OPTIONS}
          placeholder="Select option or add your own"
          values={propertyDetails[UNIT_TYPES] || []}
        />

        <Multiline
          id={DESCRIPTION}
          label={FIELD_LABELS[DESCRIPTION]}
          name={DESCRIPTION}
          onBlur={handleOnBlur}
          onChange={handleUpdatePropertyDetails}
          placeholder="What should prospective renters know about your community?"
          value={propertyDetails[DESCRIPTION]}
          variant="outlined"
        />
      </Flex>
    </DividedTabSection>
  );
};

PropertyDetails.propTypes = {
  clearFieldErrorMessage: PropTypes.func,
  propertyDetails: propertyDetailsPropTypes,
  updatePropertyDetails: PropTypes.func,
  validationErrorMessages: PropTypes.objectOf(PropTypes.string),
};

export default PropertyDetails;
